import { z } from "zod";
import { emailSchema } from "./slug.js";

/**
 * Verkoop van Klantpunt zelf (Platformbeheer, docs/VERKOOP.md, issue #242): klanten (CRM light),
 * prijslijst en verkopergegevens. Offertes, online akkoord en facturatie volgen in latere stappen.
 * Dit gaat over bedrijven die Klantpunt (willen) afnemen, niet over het adresboek van een organisatie.
 */

export const SALES_STAGES = ["lead", "demo", "offer", "client", "lost"] as const;
export type SalesStage = (typeof SALES_STAGES)[number];
export const SALES_STAGE_LABELS: Record<SalesStage, string> = {
  lead: "Lead",
  demo: "Demo gepland",
  offer: "Offerte",
  client: "Klant",
  lost: "Verloren",
};

/** Suggesties voor de soort klant (vrij veld). */
export const SALES_CUSTOMER_TYPES = [
  "Technisch beheerder",
  "Installateur",
  "Inspectie- of adviesbureau",
  "Keuringsbedrijf",
  "Schoolbestuur",
  "Zorg",
  "Gemeente",
  "VvE-beheerder",
  "Eigenaar of belegger",
  "Bedrijf of locatie",
] as const;

export const SALES_LOG_KINDS = ["note", "call", "mail", "meeting", "stage"] as const;
export type SalesLogKind = (typeof SALES_LOG_KINDS)[number];
export const SALES_LOG_KIND_LABELS: Record<SalesLogKind, string> = {
  note: "Notitie",
  call: "Gebeld",
  mail: "Mail",
  meeting: "Afspraak",
  stage: "Fase",
};
/** Wat je zelf kunt toevoegen; `stage` schrijft de server bij een fasewissel. */
export const SALES_LOG_INPUT_KINDS = ["note", "call", "mail", "meeting"] as const;

const isoDate = z.iso.date();
const text = (max: number) => z.string().trim().max(max);
const count = z.number().int().min(0).max(100_000);

/** Velden zonder defaults: wat ontbreekt neemt bij aanmaken de standaard van de database, bij wijzigen blijft het staan. */
const customerFields = {
  name: z.string().trim().min(1, "Naam is verplicht").max(200),
  type: text(100).optional(),
  stage: z.enum(SALES_STAGES).optional(),
  city: text(100).optional(),
  kvk: text(20).optional(),
  website: text(200).optional(),
  source: text(200).optional(),
  lostReason: text(500).optional(),
  /** Organisatie in Klantpunt, zodra de klant er een heeft. */
  orgId: z.uuid().nullable().optional(),
  buildings: count.optional(),
  clients: count.optional(),
  nextStepOn: isoDate.nullable().optional(),
  nextStepText: text(300).optional(),
  notes: text(4000).optional(),
};
export const salesCustomerCreateSchema = z.object({
  ...customerFields,
  /** Optioneel: meteen de eerste contactpersoon. */
  contact: z
    .object({
      name: z.string().trim().min(1).max(200),
      role: text(100).optional(),
      email: z.union([emailSchema, z.literal("")]).optional(),
      phone: text(40).optional(),
    })
    .nullable()
    .optional(),
});
export type SalesCustomerCreateBody = z.input<typeof salesCustomerCreateSchema>;
export const salesCustomerUpdateSchema = z.object(customerFields).partial();
export type SalesCustomerUpdateBody = z.input<typeof salesCustomerUpdateSchema>;

const contactFields = {
  name: z.string().trim().min(1, "Naam is verplicht").max(200),
  role: text(100).optional(),
  email: z.union([emailSchema, z.literal("")]).optional(),
  phone: text(40).optional(),
  isMain: z.boolean().optional(),
};
export const salesContactBodySchema = z.object(contactFields);
export const salesContactUpdateSchema = z.object(contactFields).partial();
export type SalesContactBody = z.input<typeof salesContactBodySchema>;

export const salesLogBodySchema = z.object({
  kind: z.enum(SALES_LOG_INPUT_KINDS).default("note"),
  text: z.string().trim().min(1, "Schrijf iets").max(2000),
});
export type SalesLogBody = z.input<typeof salesLogBodySchema>;

export interface SalesContactDto {
  id: string;
  name: string;
  role: string;
  email: string;
  phone: string;
  isMain: boolean;
}
export interface SalesLogDto {
  id: string;
  kind: SalesLogKind;
  text: string;
  byName: string | null;
  createdAt: string;
}
export interface SalesCustomerRow {
  id: string;
  name: string;
  type: string;
  stage: SalesStage;
  city: string;
  buildings: number;
  clients: number;
  mainContact: { name: string; role: string } | null;
  nextStepOn: string | null;
  nextStepText: string;
  orgId: string | null;
  orgName: string | null;
  updatedAt: string;
}
export interface SalesCustomerDetail extends SalesCustomerRow {
  kvk: string;
  website: string;
  source: string;
  lostReason: string;
  notes: string;
  createdAt: string;
  contacts: SalesContactDto[];
  log: SalesLogDto[];
}

/** Volgende stap is te laat als de datum vóór vandaag ligt (lokale datum als ISO-string). */
export function salesStepLate(nextStepOn: string | null, today: string): boolean {
  return !!nextStepOn && nextStepOn < today;
}

// ---------- prijslijst ----------

/**
 * Onderdelen van de prijslijst. Bedragen in centen per maand excl. btw, behalve `setup` (eenmalig).
 * Modules worden per gebouw berekend; `appModule` is de module in Klantpunt die erbij hoort
 * (null = onderdeel van de basis of nog geen eigen module).
 */
export const SALES_MODULES = [
  {
    key: "keuringen",
    label: "Keuringen en onderhoud",
    description: "Verplichtingen per asset, vervaldatums, certificaten",
    appModule: null,
  },
  { key: "mjop", label: "MJOP", description: "Meerjarenbegroting en reservering uit de assets", appModule: "mjop" },
  {
    key: "conditie",
    label: "Conditiemeting NEN 2767",
    description: "Gebreken, scores 1–6, voorstel voor het MJOP",
    appModule: "condition",
  },
  {
    key: "dossier",
    label: "Dossier",
    description: "Documenten en tekeningen met versies en geldigheid",
    appModule: "dossier",
  },
  {
    key: "schermen",
    label: "Informatieschermen",
    description: "Tv in de lobby met meldingen en planning",
    appModule: null,
  },
  {
    key: "materialen",
    label: "Materialen",
    description: "Artikellijst, voorraad per gebouw, afboeken",
    appModule: "materials",
  },
  {
    key: "meters",
    label: "Meterstanden",
    description: "Opnamerondes met foto, afwijkingen, Excel",
    appModule: "meters",
  },
  {
    key: "contracten",
    label: "Contracten",
    description: "Onderhoudscontracten, posten, dekking",
    appModule: "contracts",
  },
] as const;
export type SalesModuleKey = (typeof SALES_MODULES)[number]["key"];

export const SALES_BASE_PRICES = [
  {
    key: "org",
    label: "Organisatie",
    unit: "per organisatie",
    description: "Startprijs; de gratis instap heeft geen startprijs",
  },
  {
    key: "client",
    label: "Klant",
    unit: "per klant",
    description: "Opdrachtgever met eigen omgeving; eigen gebouwen tellen niet",
  },
  {
    key: "building",
    label: "Basis",
    unit: "per gebouw",
    description: "Gebouwen, bevindingen, assets, adresboek, inbox, planning",
  },
  { key: "allin", label: "All-in", unit: "per gebouw", description: "Basis en alle modules" },
  {
    key: "setup",
    label: "Inrichting en import",
    unit: "eenmalig",
    description: "Plattegronden, assets en keuringen overzetten",
  },
] as const;
export type SalesBasePriceKey = (typeof SALES_BASE_PRICES)[number]["key"];
export type SalesPriceKey = SalesBasePriceKey | SalesModuleKey;
export const SALES_PRICE_KEYS = [
  ...SALES_BASE_PRICES.map((p) => p.key),
  ...SALES_MODULES.map((m) => m.key),
] as SalesPriceKey[];

/** Voorstel 30-10-2026 (nog niet definitief); Meterstanden en Contracten ingeschat. */
export const DEFAULT_SALES_PRICES: Record<SalesPriceKey, number> = {
  org: 4900,
  client: 1000,
  building: 1900,
  allin: 4500,
  setup: 75000,
  keuringen: 900,
  mjop: 700,
  conditie: 700,
  dossier: 500,
  schermen: 500,
  materialen: 400,
  meters: 300,
  contracten: 500,
};

const cents = z.number().int().min(0).max(10_000_000);
export const salesPricesSchema = z.object(
  Object.fromEntries(SALES_PRICE_KEYS.map((k) => [k, cents])) as Record<SalesPriceKey, typeof cents>,
);
export type SalesPrices = z.infer<typeof salesPricesSchema>;

/** Wie verkoopt: komt op offerte, overeenkomst en factuur. Nog geen eigen bedrijf? Later invullen. */
export const salesSellerSchema = z.object({
  tradeName: text(200).default("Klantpunt"),
  legalName: text(200).default(""),
  kvk: text(20).default(""),
  vat: text(30).default(""),
  address: text(300).default(""),
  postcode: text(20).default(""),
  city: text(100).default(""),
  iban: text(40).default(""),
  email: z.union([emailSchema, z.literal("")]).default(""),
  phone: text(40).default(""),
  website: text(200).default("klantpunt.io"),
});
export type SalesSeller = z.infer<typeof salesSellerSchema>;

export const salesSettingsSchema = z.object({ prices: salesPricesSchema, seller: salesSellerSchema });
export type SalesSettings = z.infer<typeof salesSettingsSchema>;
export const DEFAULT_SALES_SETTINGS: SalesSettings = {
  prices: DEFAULT_SALES_PRICES,
  seller: salesSellerSchema.parse({}),
};

/** Ontbrekende gegevens van de verkoper voor een echte offerte (lege lijst = compleet). */
export function sellerMissing(s: SalesSeller): string[] {
  const out: string[] = [];
  if (!s.legalName) out.push("bedrijfsnaam");
  if (!s.kvk) out.push("KvK-nummer");
  if (!s.vat) out.push("btw-nummer");
  if (!s.address || !s.city) out.push("adres");
  if (!s.iban) out.push("IBAN");
  if (!s.email) out.push("e-mailadres");
  return out;
}
